import { code } from "@spscommerce/utils";

export const SpsLabelExamples = {
    basic: {
        label: "Basic",
        examples: {
            basic: {
                template: code`
                    <form [formGroup]="form">
                        <sps-label for="companyName">
                            Company Name
                        </sps-label>
                        <sps-text-input formControlName="companyName"></sps-text-input>
                    </form>
                `,
                class: code`
                    class DemoComponent implements OnInit {
                        form: FormGroup;

                        constructor(private formBuilder: FormBuilder) {}

                        ngOnInit() {
                            this.form = this.formBuilder.group({
                                companyName: [""]
                            });
                        }
                    }
                `
            }
        }
    },
    suggested: {
        label: "Strongly Suggested",
        examples: {
            basic: {
                template: code`
                    <form [formGroup]="form">
                        <sps-label for="companyName" stronglySuggested>
                            Company Name
                        </sps-label>
                        <sps-text-input formControlName="companyName"></sps-text-input>
                    </form>
                `,
                class: code`
                    class DemoComponent implements OnInit {
                        form: FormGroup;

                        constructor(private formBuilder: FormBuilder) {}

                        ngOnInit() {
                            this.form = this.formBuilder.group({
                                companyName: [""]
                            });
                        }
                    }
                `
            }
        }
    },
    required: {
        label: "Required",
        examples: {
            defaultMsg: {
                description: "<p>Default error message</p>",
                template: code`
                    <form [formGroup]="form">
                        <sps-label for="companyName">
                            Company Name
                        </sps-label>
                        <sps-text-input formControlName="companyName"></sps-text-input>
                    </form>
                `,
                class: code`
                    class DemoComponent implements OnInit {
                        form: FormGroup;

                        constructor(private formBuilder: FormBuilder) {}

                        ngOnInit() {
                            this.form = this.formBuilder.group({
                                companyName: ["", Validators.required]
                            });
                        }
                    }
                `
            },
            customMsg: {
                description: "<p>Custom error message</p>",
                template: code`
                    <form [formGroup]="form">
                        <sps-label for="companyName">
                            Company Name
                            <sps-label-error *ngIf="form.get('companyName').hasError('required')">
                                Please enter a company name.
                            </sps-label-error>
                        </sps-label>
                        <sps-text-input formControlName="companyName"></sps-text-input>
                    </form>
                `,
                class: code`
                    class DemoComponent implements OnInit {
                        form: FormGroup;

                        constructor(private formBuilder: FormBuilder) {}

                        ngOnInit() {
                            this.form = this.formBuilder.group({
                                companyName: ["", Validators.required]
                            });
                        }
                    }
                `
            }
        }
    },
    help: {
        label: "Help Information",
        examples: {
            defaultIcon: {
                description: "<p>Default icon</p>",
                template: code`
                    <form [formGroup]="form">
                        <sps-label for="companyName">
                            Company Name
                            <sps-label-help>
                                Pretend there's something helpful written here.
                            </sps-label-help>
                        </sps-label>
                        <sps-text-input formControlName="companyName"></sps-text-input>
                    </form>
                `,
                class: code`
                    class DemoComponent implements OnInit {
                        form: FormGroup;

                        constructor(private formBuilder: FormBuilder) {}

                        ngOnInit() {
                            this.form = this.formBuilder.group({
                                companyName: [""]
                            });
                        }
                    }
                `
            },
            customIcon: {
                description: "<p>Custom icon</p>",
                template: code`
                    <form [formGroup]="form">
                        <sps-label for="companyName">
                            Company Name
                            <sps-label-help icon="exchange" iconColor="red200">
                                Pretend there's something helpful written here.
                            </sps-label-help>
                        </sps-label>
                        <sps-text-input formControlName="companyName"></sps-text-input>
                    </form>
                `,
                class: code`
                    class DemoComponent implements OnInit {
                        form: FormGroup;

                        constructor(private formBuilder: FormBuilder) {}

                        ngOnInit() {
                            this.form = this.formBuilder.group({
                                companyName: [""]
                            });
                        }
                    }
                `
            }
        }
    },
    validation: {
        label: "Validation",
        examples: {
            basic: {
                template: code`
                    <form [formGroup]="form">
                        <sps-label for="companyName">
                            Company Name
                            <sps-label-error *ngIf="form.get('companyName').hasError('minlength')">
                                Company Name must be at least 7 characters.
                            </sps-label-error>
                        </sps-label>
                        <sps-text-input formControlName="companyName"></sps-text-input>
                    </form>
                `,
                class: code`
                    class DemoComponent implements OnInit {
                        form: FormGroup;

                        constructor(private formBuilder: FormBuilder) {}

                        ngOnInit() {
                            this.form = this.formBuilder.group({
                                companyName: ["Target", Validators.minLength(7)]
                            });

                            // Make validation error visible for the purpose of this demo
                            this.form.get("companyName").markAsDirty();
                        }
                    }
                `
            }
        }
    },
    combinations: {
        label: "Combinations",
        examples: {
            combo1: {
                template: code`
                    <form [formGroup]="form">
                        <sps-label for="companyName" stronglySuggested>
                            Company Name
                            <sps-label-help>
                                Pretend there's something helpful written here.
                            </sps-label-help>
                            <sps-label-error *ngIf="form.get('companyName').hasError('minlength')">
                                Company Name must be at least 7 characters.
                            </sps-label-error>
                        </sps-label>
                        <sps-text-input formControlName="companyName"></sps-text-input>
                    </form>
                `,
                class: code`
                    class DemoComponent implements OnInit {
                        form: FormGroup;

                        constructor(private formBuilder: FormBuilder) {}

                        ngOnInit() {
                            this.form = this.formBuilder.group({
                                companyName: ["Target", Validators.minLength(7)]
                            });

                            // Make validation error visible for the purpose of this demo
                            this.form.get("companyName").markAsDirty();
                        }
                    }
                `
            },
            combo2: {
                template: code`
                    <form [formGroup]="form">
                        <sps-label for="companyName">
                            Company Name
                            <sps-label-help>
                                Pretend there's something helpful written here.
                            </sps-label-help>
                        </sps-label>
                        <sps-text-input formControlName="companyName"></sps-text-input>
                    </form>
                `,
                class: code`
                    class DemoComponent implements OnInit {
                        form: FormGroup;

                        constructor(private formBuilder: FormBuilder) {}

                        ngOnInit() {
                            this.form = this.formBuilder.group({
                                companyName: ["", Validators.required]
                            });
                        }
                    }
                `
            },
        }
    },
};
